/* eslint-disable max-lines */
import { MARKETPLACE_HEADERS } from "constants/bill";
import {
    ANDROID_THIRD_PARTY_AVAILABLE_MIN_VERSION,
    API_ENDPOINTS,
    DINEOUT_CART_API_HOST,
    IOS_THIRD_PARTY_AVAILABLE_MIN_VERSION,
} from "constants/index";
import { STORAGE_KEYS } from "constants/StorageKeys";
import { isEmpty, safeJsonParse } from "@swiggy-private/js-utils";
import type { GetSwiggyHeadersResponsePayload } from "@swiggy-private/native-bridge";
import { STORAGE_TYPES } from "@swiggy-private/native-bridge";
import { storeItemsToSessionStorage, webStorage } from "@swiggy-private/portal-dineout-ui";
import { WebStorageLocation } from "types";
import { getDefaultApiHeaders } from "./api";
import { getBridgeSdk } from "./bridgeSdk";
import { isAndroid, isApp, isBrowserEnv, isIOS } from "./browser";
import { convertKeysToLowerCase } from "./helper";
import { IOrderPendingParams } from "./paymentTypes";
import { getFromStorage, deleteFromStorage } from "./storage";

// Check if local cart has items
export const hasCartItems = (): boolean => {
    if (typeof window === "undefined") return false;
    try {
        const localCart = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.LOCAL_CART) || "{}");
        // Check both that localCart exists AND has items in cartItems array
        return !isEmpty(localCart) && !isEmpty(localCart?.cartItems);
    } catch {
        return false;
    }
};

export const getParentOrderIdFromStorage = (tableId: string, restaurantId: string): string => {
    try {
        const tableConfig = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.TABLE_CONFIG) || "{}");
        const storedTableId = tableConfig?.tableId || "";
        const storedRestaurantId = tableConfig?.restaurantId || "";

        // Only return parentOrderId if tableId and restaurantId match
        if (storedTableId === tableId && storedRestaurantId === restaurantId) {
            return sessionStorage.getItem("parent_order_id") || "";
        }
        return "";
    } catch {
        return "";
    }
};

export const manageParentOrderIdStorage = (newTableId: string, newRestaurantId: string, newParentOrderId: string) => {
    const tableConfig = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.TABLE_CONFIG) || "{}");
    const storedTableId = tableConfig?.tableId || "";
    const storedRestaurantId = tableConfig?.restaurantId || "";
    const storedParentOrderId = sessionStorage.getItem("parent_order_id") || "";

    if (storedTableId !== newTableId || storedRestaurantId !== newRestaurantId) {
        // If tableId or restaurantId changes, remove parentOrderId
        sessionStorage.removeItem("parent_order_id");

        // Store new parentOrderId only if it's not empty
        if (newParentOrderId) {
            storeItemsToSessionStorage({ parent_order_id: newParentOrderId });
        }
    } else if (newParentOrderId && newParentOrderId !== storedParentOrderId) {
        // Store the new parentOrderId only if it's different and not empty
        storeItemsToSessionStorage({ parent_order_id: newParentOrderId });
    }
};

export const getConfirmOrderParams = ({
    orderId,
    paymentTransactionId,
    paymentId,
    headers,
}: {
    orderId?: string;
    paymentTransactionId?: string;
    paymentId?: string;
    headers: Record<string, string>;
}): IOrderPendingParams => {
    return {
        host: DINEOUT_CART_API_HOST,
        method: "POST",
        endpoint: API_ENDPOINTS.EXTERNAL_CONFIRM_ORDER,
        headers,
        body: {
            order_id: orderId || "",
            payment_transaction_id: paymentTransactionId || "",
            ...(!isEmpty(paymentId) ? { payment_id: paymentId } : {}),
        },
    };
};

export const getPaymentPendingParamsFromLocal = async (): Promise<
    { requestObj: IOrderPendingParams } | null | undefined
> => {
    if (isBrowserEnv()) {
        const storageResponse = await getFromStorage(
            STORAGE_KEYS.PAYMENT_PENDING_PARAMS,
            WebStorageLocation.LOCAL,
            true,
        );
        const pendingParams = storageResponse?.data?.[STORAGE_KEYS.PAYMENT_PENDING_PARAMS];
        if (!isEmpty(pendingParams)) {
            return safeJsonParse<{ requestObj: IOrderPendingParams } | null>(pendingParams, null);
        }
    }
    return null;
};

export const setPaymentPendingParams = async ({
    pendingPaymentParams,
    paymentMethodName,
    marketplaceBusinessLine,
    marketplaceCategory,
}: {
    pendingPaymentParams?: IOrderPendingParams;
    paymentMethodName: string;
    marketplaceBusinessLine: string;
    marketplaceCategory: string;
}) => {
    if (isEmpty(pendingPaymentParams)) {
        return;
    }

    webStorage(
        {
            type: STORAGE_TYPES.SAVE,
            data: {
                [STORAGE_KEYS.PAYMENT_PENDING_PARAMS]: JSON.stringify({
                    paymentMethodName,
                    marketplaceBusinessLine,
                    marketplaceCategory,
                    requestObj: pendingPaymentParams,
                }),
            },
        },
        WebStorageLocation.LOCAL,
    ).catch(() => void 0);

    if (isApp()) {
        getBridgeSdk()
            .paymentInPendingState({
                paymentMethodName,
                marketplaceBusinessLine,
                marketplaceCategory,
                requestObj: pendingPaymentParams,
            })
            .catch(() => void 0);
    }
};

export const clearPaymentPendingParams = async () => {
    if (isBrowserEnv()) {
        await deleteFromStorage(STORAGE_KEYS.PAYMENT_PENDING_PARAMS, WebStorageLocation.LOCAL, true);
    }
};

export const getSuccessScreenHttpHeaders = (swiggyHeaders: Record<string, string>) => {
    const headers = {
        ...swiggyHeaders,
        ...getDefaultApiHeaders(swiggyHeaders as GetSwiggyHeadersResponsePayload),
    } as Record<string, string>;

    if (!isEmpty(headers)) {
        // delete marketplacebusinessine(both small and camleCase), this is related to fix where app uses camelCase keys for these headers, to prevent payment flow from breaking
        delete headers?.marketplaceBusinessLine;
        delete headers?.marketplacebusinessline;
        delete headers?.marketplaceCategory;
        delete headers?.marketplacecategory;
    }

    const appHeadersWithLowerCaseKeys = convertKeysToLowerCase(headers);

    return {
        ...appHeadersWithLowerCaseKeys,
        // adding these headers to prevent payment flow from breaking
        marketplaceBusinessLine: MARKETPLACE_HEADERS.BUSINESS_LINE,
        marketplaceCategory: MARKETPLACE_HEADERS.MARKETPLACE_CATEGORY,
        "x-web-checkout-flow": MARKETPLACE_HEADERS.X_WEB_CHECKOUT_FLOW,
        "service-line": MARKETPLACE_HEADERS.BUSINESS_LINE_ID,
        showsuccessscreen: "true",
    };
};

export const getAmountInPaise = (amount?: number | string) => {
    if (isEmpty(amount)) {
        return;
    }

    return Number(amount) * 100;
};

export const isWebviewUpiIntentAvailable = (swiggyHeaders?: GetSwiggyHeadersResponsePayload) => {
    if (!isApp() || isEmpty(swiggyHeaders)) {
        return false;
    }

    if (isAndroid() && Number(swiggyHeaders?.["version-code"]) >= Number(ANDROID_THIRD_PARTY_AVAILABLE_MIN_VERSION)) {
        return true;
    }

    if (isIOS() && Number(swiggyHeaders?.["version-code"]) >= Number(IOS_THIRD_PARTY_AVAILABLE_MIN_VERSION)) {
        return true;
    }

    return false;
};
